// src/components/vault/documents/id-validation/useIDValidationFlow.ts

import { useReducer, useCallback, useRef, useEffect } from "react";
import type {
  IDValidationState,
  IDValidationAction,
  ExtractedINEData,
} from "./types";
import { DOCUMENTS_WITH_ADDRESS_FLOW } from "./types";
import {
  INITIAL_VALIDATION_STATE,
  PROGRESS_INTERVAL_MS,
  getLogarithmicProgress,
} from "./constants";
import {
  VerifyIdentityResponse,
  ExtractedINEData as APIExtractedINEData,
} from "../../../../redux/actions/verificationActions";
import { vaultApi } from "../../../../api/vault.api";
import { useVerifyIdentity } from "../../../../hooks/vault/mutations/useVerifyIdentity";
import {
  summarizeVerificationResponse,
  verificationDebugError,
  verificationDebugLog,
} from "../../../../utils/verificationDebug";
import { extractErrorMessage } from "../../../../utils/extractErrorMessage";

// Valid document types for runtime validation
const VALID_DOCUMENT_TYPES = [
  "ine",
  "passport",
  "driver_license",
  "migratory_form",
] as const;
type DocumentType = (typeof VALID_DOCUMENT_TYPES)[number];

const isValidDocumentType = (type: string): type is DocumentType =>
  VALID_DOCUMENT_TYPES.includes(type as DocumentType);

type APIExtractedINEDataInput = Partial<APIExtractedINEData>;

const REQUIRED_ID_ADDRESS_FIELDS: (keyof ExtractedINEData["direccion"])[] = [
  "calle",
  "estado",
];

function getMissingExtractedAddressFields(
  extractedData: ExtractedINEData | null,
): (keyof ExtractedINEData["direccion"])[] {
  if (!extractedData) return [...REQUIRED_ID_ADDRESS_FIELDS];

  return REQUIRED_ID_ADDRESS_FIELDS.filter(
    (field) => String(extractedData.direccion[field] ?? "").trim() === "",
  );
}

export function hasSufficientExtractedAddress(
  extractedData: ExtractedINEData | null,
): boolean {
  return getMissingExtractedAddressFields(extractedData).length === 0;
}

/**
 * Normalize gender values from OCR ("H"/"M" per INE standard) to display format ("Masculino"/"Femenino")
 */
function normalizeGender(raw: string): string {
  if (!raw) return "";
  const normalized = raw.trim().toUpperCase();
  if (["H", "HOMBRE", "MASCULINO", "MALE"].includes(normalized))
    return "Masculino";
  if (["M", "MUJER", "FEMENINO", "FEMALE"].includes(normalized))
    return "Femenino";
  return raw;
}

function isNonEmptyRecord(value: unknown): value is Record<string, any> {
  return (
    Boolean(value) &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value as object).length > 0
  );
}

function getStoredIdentityData(
  verificationData: any,
): APIExtractedINEDataInput | null {
  if (!verificationData) return null;

  // Precedence: confirmed_data (user-edited) > extracted_data (already
  // normalized to the app's format by the backend) > legacy raw-ocr parsing.
  const confirmed = verificationData.confirmed_data;
  if (isNonEmptyRecord(confirmed)) return confirmed;

  const storedExtracted = verificationData.extracted_data;
  if (isNonEmptyRecord(storedExtracted)) return storedExtracted;

  // Legacy fallback: old records persisted before extracted_data was stored
  // alongside the raw OCR response. Parse the raw provider shapes directly.
  const ocr = verificationData.ocr;
  if (!ocr) return null;

  const personal = ocr.personal_information ?? ocr.personal_info ?? {};
  const name = personal.name ?? {};
  const address = ocr.address ?? ocr.address_details ?? {};
  const docData =
    ocr.document_identification_data ??
    ocr.electoral_data ??
    ocr.explicit_data ??
    {};
  const expirationYear =
    ocr.validity_intelligence?.expiration_year ?? docData.expiration_year;

  return {
    nombre:
      name.name ??
      name.first_name ??
      personal.first_name ??
      personal.given_names ??
      "",
    segundo_nombre: "",
    apellido_paterno:
      name.paternal_last_name ??
      personal.paternal_surname ??
      personal.first_surname ??
      "",
    apellido_materno:
      name.maternal_last_name ??
      personal.maternal_surname ??
      personal.second_surname ??
      "",
    curp: personal.curp ?? ocr.document_details?.curp ?? "",
    rfc: personal.rfc ?? "",
    fecha_nacimiento: personal.date_of_birth ?? "",
    genero: personal.gender ?? personal.gender_standardized ?? "",
    clave_elector: docData.elector_key ?? "",
    seccion_electoral: docData.electoral_section ?? docData.section ?? "",
    vigencia:
      docData.dates?.expiration_date ??
      (expirationYear ? `${expirationYear}-12-31` : ""),
    direccion: {
      calle: address.street ?? "",
      numero_exterior:
        address.exterior_number ??
        address.external_number ??
        address.number ??
        "",
      numero_interior: address.interior_number ?? address.internal_number ?? "",
      colonia: address.neighborhood ?? address.colony ?? "",
      codigo_postal: address.zip ?? address.zip_code ?? "",
      municipio: address.municipality ?? address.municipality_delegation ?? "",
      estado: address.state ?? "",
    },
  };
}

/**
 * Reducer for the ID validation flow state machine
 */
function validationReducer(
  state: IDValidationState,
  action: IDValidationAction,
): IDValidationState {
  switch (action.type) {
    case "START_VALIDATION":
      return {
        ...state,
        currentStep: "validating",
        documentType: action.documentType,
        hasProofOfAddress: action.hasProofOfAddress,
        validationProgress: 0,
        error: null,
      };

    case "UPDATE_PROGRESS":
      return {
        ...state,
        validationProgress: action.progress,
      };

    case "VALIDATION_COMPLETE": {
      const isINE = DOCUMENTS_WITH_ADDRESS_FLOW.includes(
        state.documentType || "",
      );
      const showAddressCheck = isINE && state.hasProofOfAddress;
      return {
        ...state,
        currentStep: showAddressCheck ? "address_check" : "completed",
        extractedData: action.extractedData,
        validationProgress: 100,
      };
    }

    case "REQUIRE_PROOF_OF_ADDRESS":
      return {
        ...state,
        currentStep: "completed",
        extractedData: action.extractedData,
        isAddressUpdated: false,
        validationProgress: 100,
      };

    case "SET_ADDRESS_UPDATED":
      return {
        ...state,
        currentStep: "completed",
        isAddressUpdated: action.isUpdated,
      };

    case "COMPLETE_FLOW":
      return {
        ...state,
        currentStep: "completed",
      };

    case "RESET_FLOW":
      return INITIAL_VALIDATION_STATE;

    case "VALIDATION_ERROR":
      return {
        ...state,
        currentStep: "upload",
        validationProgress: 0,
        error: action.error,
        invalidReason: null,
      };

    case "SET_PENDING_REVIEW":
      return {
        ...state,
        currentStep: "pending_review",
        validationProgress: 100,
        error: null,
        invalidReason: null,
      };

    case "SET_REJECTED":
      return {
        ...state,
        currentStep: "rejected",
        invalidReason: action.invalidReason,
        validationProgress: 0,
        error: null,
      };

    case "SET_COMPLETED":
      return {
        ...state,
        currentStep: "completed",
        validationProgress: 100,
        error: null,
        invalidReason: null,
        isAddressUpdated: null,
      };

    case "RESTORE_ADDRESS_CHECK":
      // Restore address_check step from backend on page reload
      // Sets documentType to "ine" since only INE has address_check flow
      // Trust the backend's workflow_step decision unconditionally
      return {
        ...state,
        currentStep: "address_check",
        documentType: "ine",
        extractedData: action.extractedData,
        validationProgress: 100,
        error: null,
        invalidReason: null,
      };

    case "SET_HAS_PROOF_OF_ADDRESS":
      return {
        ...state,
        hasProofOfAddress: action.hasProofOfAddress,
      };

    default:
      return state;
  }
}

/**
 * Hook to manage the ID validation flow
 *
 * Flow:
 * 1. User uploads document -> START_VALIDATION
 * 2. Loader runs for 10 seconds -> UPDATE_PROGRESS (multiple times)
 * 3. Validation complete:
 *    - If INE: -> address_check step (SET_ADDRESS_UPDATED)
 *    - If other: -> completed step
 * 4. Address check result: -> completed step (data confirmation moved to separate DataValidation step)
 */
export function useIDValidationFlow() {
  const [state, dispatch] = useReducer(
    validationReducer,
    INITIAL_VALIDATION_STATE,
  );
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const requirementIdRef = useRef<string | undefined>(undefined);
  const { mutateAsync: verifyIdentity } = useVerifyIdentity();

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  /**
   * Persist workflow step to backend
   * Called when user navigates between steps to enable page reload recovery
   * When step is "completed" and confirmedData is provided, backend will
   * pre-fill the tenant-form requirement with the confirmed OCR data.
   */
  const persistWorkflowStep = useCallback(
    async (step: string, confirmedData?: Record<string, any>) => {
      if (!requirementIdRef.current) {
        return false;
      }
      try {
        await vaultApi.updateWorkflowStep(requirementIdRef.current, {
          workflow_step: step,
          confirmed_data: confirmedData,
        });
        return true;
      } catch (error) {
        verificationDebugError(
          "idValidation.persistWorkflowStep.error",
          error,
          {
            requirementId: requirementIdRef.current,
            workflowStep: step,
            hasConfirmedData: Boolean(confirmedData),
          },
        );
        console.error("Failed to persist workflow step:", error);
        return false;
      }
    },
    [],
  );

  /**
   * Transform API response to frontend ExtractedINEData format
   */
  const transformToExtractedINEData = useCallback(
    (data: APIExtractedINEDataInput): ExtractedINEData => ({
      nombre: data.nombre || "",
      segundoNombre: data.segundo_nombre || (data as any).segundoNombre || "",
      apellidoPaterno:
        data.apellido_paterno || (data as any).apellidoPaterno || "",
      apellidoMaterno:
        data.apellido_materno || (data as any).apellidoMaterno || "",
      curp: data.curp || "",
      rfc: data.rfc || "",
      claveElector: data.clave_elector || (data as any).claveElector || "",
      fechaNacimiento:
        data.fecha_nacimiento || (data as any).fechaNacimiento || "",
      seccionElectoral:
        data.seccion_electoral || (data as any).seccionElectoral || "",
      vigencia: data.vigencia || "",
      genero: normalizeGender(data.genero || ""),
      email: data.email || "",
      phone_number: data.phone_number || "",
      dial_code: data.dial_code === "+1" ? "+1" : "+52",
      direccion: {
        calle: data.direccion?.calle || "",
        numeroExterior:
          data.direccion?.numero_exterior ||
          (data.direccion as any)?.numeroExterior ||
          "",
        numeroInterior:
          data.direccion?.numero_interior ||
          (data.direccion as any)?.numeroInterior ||
          "",
        colonia: data.direccion?.colonia || "",
        codigoPostal:
          data.direccion?.codigo_postal ||
          (data.direccion as any)?.codigoPostal ||
          "",
        municipio: data.direccion?.municipio || "",
        estado: data.direccion?.estado || "",
      },
    }),
    [],
  );

  /**
   * Start the validation process
   * Calls real OCR API and shows progress animation during the request
   */
  const startValidation = useCallback(
    async (
      documentType: string,
      frontFile?: File,
      backFile?: File,
      requirementId?: string,
      hasProofOfAddress: boolean = true,
    ): Promise<VerifyIdentityResponse | void> => {
      // Validate document type at runtime before proceeding
      if (!isValidDocumentType(documentType)) {
        throw new Error(`Tipo de documento invalido: ${documentType}`);
      }

      // Store requirement ID for future persistence
      requirementIdRef.current = requirementId;
      verificationDebugLog("idValidation.start", {
        documentType,
        requirementId,
        hasBackFile: Boolean(backFile),
        hasProofOfAddress,
        frontFile: frontFile
          ? {
              name: frontFile.name,
              size: frontFile.size,
              type: frontFile.type,
            }
          : null,
        backFile: backFile
          ? {
              name: backFile.name,
              size: backFile.size,
              type: backFile.type,
            }
          : null,
      });

      dispatch({ type: "START_VALIDATION", documentType, hasProofOfAddress });

      startTimeRef.current = Date.now();

      // Clear any existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Start progress animation with logarithmic curve (fast start, gradual slowdown)
      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        const progress = getLogarithmicProgress(elapsed);
        dispatch({ type: "UPDATE_PROGRESS", progress });
      }, PROGRESS_INTERVAL_MS);

      try {
        // Call real API (documentType is already validated as DocumentType)
        const formData = new FormData();
        formData.append("document_type", documentType);
        if (requirementId) formData.append("requirement_id", requirementId);
        // Once requirementId is present Django reads the originals already
        // attached to that requirement (signed references in GCS, stored-file
        // fallback locally). Files remain optional only for the legacy direct
        // verification path that has no persisted requirement.
        if (frontFile) formData.append("front_image", frontFile);
        if (backFile) formData.append("back_image", backFile);

        const response = await verifyIdentity(formData);
        verificationDebugLog(
          "idValidation.response",
          {
            documentType,
            requirementId,
            response: summarizeVerificationResponse(response),
          },
          response,
        );

        // Clear progress interval
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        // Set progress to 100%
        dispatch({ type: "UPDATE_PROGRESS", progress: 100 });

        // Small delay for UX (show 100% completion)
        await new Promise((resolve) => setTimeout(resolve, 500));

        switch (response.status) {
          case "valid": {
            // Transform response to ExtractedINEData format
            const extractedData = response.extracted_data
              ? transformToExtractedINEData(response.extracted_data)
              : null;
            const isINE = DOCUMENTS_WITH_ADDRESS_FLOW.includes(documentType);
            const requiresProofOfAddress =
              isINE &&
              hasProofOfAddress &&
              !hasSufficientExtractedAddress(extractedData);

            if (requiresProofOfAddress) {
              verificationDebugLog("idValidation.decision", {
                documentType,
                requirementId,
                status: response.status,
                nextStep: "completed",
                requiresProofOfAddress,
                hasSufficientExtractedAddress: false,
              });
              dispatch({
                type: "REQUIRE_PROOF_OF_ADDRESS",
                extractedData,
              });
              const persisted = await persistWorkflowStep("completed");
              if (!persisted) {
                dispatch({
                  type: "VALIDATION_ERROR",
                  error:
                    "No se pudo sincronizar el resultado de la identificación. Intenta nuevamente.",
                });
                return;
              }
              break;
            }

            // Proceed with address flow (INE with POA) or complete (other docs / ID-only)
            verificationDebugLog("idValidation.decision", {
              documentType,
              requirementId,
              status: response.status,
              nextStep:
                isINE && hasProofOfAddress ? "address_check" : "completed",
              requiresProofOfAddress,
              hasSufficientExtractedAddress:
                hasSufficientExtractedAddress(extractedData),
            });
            dispatch({ type: "VALIDATION_COMPLETE", extractedData });
            // Persist workflow step for page reload recovery
            const persisted = await persistWorkflowStep(
              isINE && hasProofOfAddress ? "address_check" : "completed",
            );
            if (!persisted) {
              dispatch({
                type: "VALIDATION_ERROR",
                error:
                  "No se pudo sincronizar el resultado de la identificación. Intenta nuevamente.",
              });
              return;
            }
            break;
          }

          case "pending_review": {
            // Pending review - treat like valid, let user continue the flow
            const pendingExtractedData = response.extracted_data
              ? transformToExtractedINEData(response.extracted_data)
              : null;
            const isPendingINE =
              DOCUMENTS_WITH_ADDRESS_FLOW.includes(documentType);
            const requiresProofOfAddress =
              isPendingINE &&
              hasProofOfAddress &&
              !hasSufficientExtractedAddress(pendingExtractedData);

            if (requiresProofOfAddress) {
              verificationDebugLog("idValidation.decision", {
                documentType,
                requirementId,
                status: response.status,
                nextStep: "completed",
                requiresProofOfAddress,
                hasSufficientExtractedAddress: false,
              });
              dispatch({
                type: "REQUIRE_PROOF_OF_ADDRESS",
                extractedData: pendingExtractedData,
              });
              const persisted = await persistWorkflowStep("completed");
              if (!persisted) {
                dispatch({
                  type: "VALIDATION_ERROR",
                  error:
                    "No se pudo sincronizar el resultado de la identificación. Intenta nuevamente.",
                });
                return;
              }
              break;
            }

            verificationDebugLog("idValidation.decision", {
              documentType,
              requirementId,
              status: response.status,
              nextStep:
                isPendingINE && hasProofOfAddress
                  ? "address_check"
                  : "completed",
              requiresProofOfAddress,
              hasSufficientExtractedAddress:
                hasSufficientExtractedAddress(pendingExtractedData),
            });
            dispatch({
              type: "VALIDATION_COMPLETE",
              extractedData: pendingExtractedData,
            });
            const persisted = await persistWorkflowStep(
              isPendingINE && hasProofOfAddress ? "address_check" : "completed",
            );
            if (!persisted) {
              dispatch({
                type: "VALIDATION_ERROR",
                error:
                  "No se pudo sincronizar el resultado de la identificación. Intenta nuevamente.",
              });
              return;
            }
            break;
          }

          case "invalid":
            // Show rejection message with invalid_reason, allow re-upload
            verificationDebugLog("idValidation.decision", {
              documentType,
              requirementId,
              status: response.status,
              nextStep: "rejected",
              invalidReason: response.invalid_reason,
            });
            dispatch({
              type: "SET_REJECTED",
              invalidReason: response.invalid_reason,
            });
            await persistWorkflowStep("rejected");
            break;

          default:
            // Fallback - treat unknown status as error
            dispatch({
              type: "VALIDATION_ERROR",
              error: `Estado de validación desconocido: ${response.status}`,
            });
        }

        return response;
      } catch (error: any) {
        // Clear progress interval
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        // Dispatch error action with error message
        const errorMessage = extractErrorMessage(
          error,
          "Error al verificar el documento",
        );
        verificationDebugError("idValidation.error", error, {
          documentType,
          requirementId,
          errorMessage,
        });
        dispatch({ type: "VALIDATION_ERROR", error: errorMessage });
        // Don't re-throw - let state handle the error display
      }
    },
    [transformToExtractedINEData, persistWorkflowStep, verifyIdentity],
  );

  /**
   * Set whether the address on the ID is updated
   * @param isUpdated - true if address is current, false if needs proof of address
   */
  const setAddressUpdated = useCallback(
    async (isUpdated: boolean) => {
      // Don't pass extractedData as confirmed_data here — the DataValidation step
      // is responsible for letting the user confirm/edit and persist confirmed_data.
      const persisted = await persistWorkflowStep("completed");
      if (!persisted) {
        return false;
      }
      dispatch({ type: "SET_ADDRESS_UPDATED", isUpdated });
      return true;
    },
    [persistWorkflowStep],
  );

  /**
   * Complete the flow manually
   */
  const completeFlow = useCallback(() => {
    dispatch({ type: "COMPLETE_FLOW" });
  }, []);

  /**
   * Reset the flow to initial state
   */
  const resetFlow = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    dispatch({ type: "RESET_FLOW" });
  }, []);

  /**
   * Cancel the validation (e.g., when navigating away)
   */
  const cancelValidation = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    dispatch({ type: "RESET_FLOW" });
  }, []);

  /**
   * Initialize flow state from requirement status AND workflow_step (for page reload detection)
   * Priority: workflow_step > status (workflow_step is more precise)
   */
  const initializeFromRequirement = useCallback(
    (requirement: {
      id?: string;
      status: string;
      invalid_reason?: string | null;
      workflow_step?: string | null;
      verification_data?: any;
      hasProofOfAddress?: boolean;
      documentType?: string;
      reviewAddressOnEntry?: boolean;
    }) => {
      // Store requirement ID for future persistence
      requirementIdRef.current = requirement.id;
      verificationDebugLog(
        "idValidation.restore",
        {
          requirementId: requirement.id,
          status: requirement.status,
          workflowStep: requirement.workflow_step,
          hasProofOfAddress: requirement.hasProofOfAddress ?? true,
        },
        requirement.verification_data,
      );

      // Set hasProofOfAddress before any step restoration so reducers can use it
      const hasPoa = requirement.hasProofOfAddress ?? true;
      dispatch({ type: "SET_HAS_PROOF_OF_ADDRESS", hasProofOfAddress: hasPoa });

      // PRIORITY 0: Resolve discrepancy - status="valid" overrides workflow_step="pending_review"
      // This happens when an operator manually approves a pending_review requirement
      // and the backend didn't sync the workflow_step (or the data was cached)
      if (
        requirement.status === "valid" &&
        requirement.workflow_step === "pending_review"
      ) {
        dispatch({ type: "SET_COMPLETED" });
        return;
      }

      // PRIORITY 1: Use workflow_step if available (most precise state)
      if (requirement.workflow_step) {
        switch (requirement.workflow_step) {
          case "address_check": {
            // Restore extracted data from verification_data if available
            const storedIdentityData = getStoredIdentityData(
              requirement.verification_data,
            );
            const extractedData = storedIdentityData
              ? transformToExtractedINEData(storedIdentityData)
              : null;
            if (hasPoa && !hasSufficientExtractedAddress(extractedData)) {
              dispatch({ type: "REQUIRE_PROOF_OF_ADDRESS", extractedData });
              return;
            }
            // Use RESTORE_ADDRESS_CHECK to directly set address_check step
            // This avoids the bug where VALIDATION_COMPLETE checks state.documentType (which is null after resetFlow)
            dispatch({ type: "RESTORE_ADDRESS_CHECK", extractedData });
            return;
          }
          case "data_confirmation":
          case "data_edit":
            // Legacy workflow steps from old flow - restore to completed
            dispatch({ type: "SET_COMPLETED" });
            return;
          case "pending_review": {
            // Pending review no longer blocks - treat like valid and continue the flow
            // Restore to address_check if there's OCR data (INE), otherwise complete
            const pendingStoredData = getStoredIdentityData(
              requirement.verification_data,
            );
            if (pendingStoredData) {
              const pendingExtractedData =
                transformToExtractedINEData(pendingStoredData);
              if (!hasPoa) {
                // ID-only subfolder (no proof-of-address requirement): the
                // address_check step is skipped, mirroring the runtime
                // VALIDATION_COMPLETE gate (isINE && hasProofOfAddress).
                // Restoring address_check here would strand the user on an
                // address-confirmation prompt they should never see.
                dispatch({ type: "SET_COMPLETED" });
              } else if (!hasSufficientExtractedAddress(pendingExtractedData)) {
                dispatch({
                  type: "REQUIRE_PROOF_OF_ADDRESS",
                  extractedData: pendingExtractedData,
                });
              } else {
                dispatch({
                  type: "RESTORE_ADDRESS_CHECK",
                  extractedData: pendingExtractedData,
                });
              }
            } else {
              dispatch({ type: "SET_COMPLETED" });
            }
            return;
          }
          case "rejected":
            dispatch({
              type: "SET_REJECTED",
              invalidReason: requirement.invalid_reason ?? null,
            });
            return;
          case "completed": {
            // A deliberate return from the proof-of-address step can revisit
            // the INE question without changing validated identity evidence.
            if (
              requirement.reviewAddressOnEntry &&
              requirement.documentType === "ine" &&
              hasPoa &&
              ["valid", "pending_review"].includes(requirement.status)
            ) {
              const storedData = getStoredIdentityData(
                requirement.verification_data,
              );
              const extractedData = storedData
                ? transformToExtractedINEData(storedData)
                : null;
              if (hasSufficientExtractedAddress(extractedData)) {
                dispatch({ type: "RESTORE_ADDRESS_CHECK", extractedData });
                return;
              }
            }
            dispatch({ type: "SET_COMPLETED" });
            return;
          }
          case "upload":
          case "validating":
            // A synchronous validation request cannot survive a reload. Do
            // not promote its temporary pending_review upload status to a
            // completed identity flow.
            dispatch({ type: "RESET_FLOW" });
            return;
          default:
            // Fall through to status-based logic
            break;
        }
      }

      // PRIORITY 2: Fallback to status-based initialization (existing logic)
      switch (requirement.status) {
        case "pending_review":
          // Match develop for legacy/manual-review rows without workflow
          // metadata: pending_review is terminal and must not strand the user
          // on a disabled upload form.
          dispatch({ type: "SET_COMPLETED" });
          break;
        case "invalid":
          dispatch({
            type: "SET_REJECTED",
            invalidReason: requirement.invalid_reason ?? null,
          });
          break;
        case "valid":
          dispatch({ type: "SET_COMPLETED" });
          break;
        default:
          // pending_upload or unknown -> show upload form
          dispatch({ type: "RESET_FLOW" });
      }
    },
    [transformToExtractedINEData],
  );

  return {
    state,
    // Actions
    startValidation,
    setAddressUpdated,
    completeFlow,
    resetFlow,
    cancelValidation,
    initializeFromRequirement,
    // Computed values
    isValidating: state.currentStep === "validating",
    isCompleted: state.currentStep === "completed",
    showAddressCheck: state.currentStep === "address_check",
    isPendingReview: state.currentStep === "pending_review",
    isRejected: state.currentStep === "rejected",
    shouldUnlockSteps: state.currentStep === "completed",
    shouldNavigateToProofOfAddress:
      state.currentStep === "completed" && state.isAddressUpdated === false,
    shouldNavigateToDataValidation:
      state.currentStep === "completed" && state.isAddressUpdated === true,
    hasError: state.error !== null,
    error: state.error,
    invalidReason: state.invalidReason,
  };
}

export type UseIDValidationFlowReturn = ReturnType<typeof useIDValidationFlow>;
